import React, { useState } from "react";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { LucidePlus, LucideX } from "lucide-react";
import type { ShopMenuItem, ShopMenuItemType } from "../../../../../../domain/types/company-shop";
import type { CategoryDto } from "../../../../../../domain/types/company/company-product-system";
import { DEFAULT_HEADER_MENU_ITEMS, DEFAULT_MENU_HREF_LABELS, LINKABLE_HEADER_MENU_KEYS } from "../../constants";
import { DragHandleIcon, Field, Segmented, SelectField, TextField, ToggleSwitch } from "../ui/SettingsUI";

/*
  헤더 메뉴 편집 (2026-10-26).
  - 기본 메뉴: 이름은 바꿀 수 없다. 노출 · 순서와 (책자주문 · 낱장주문 · 빠른 견적 · 편집디자인 · 마이페이지만) 이동할 곳을 고른다.
  - 추가 메뉴: 카테고리 · 상품을 연결하면 그 이름을 그대로 쓰고, 직접 링크는 이름과 주소를 자유롭게 정한다. 모두 지울 수 있다.
  저장 규칙은 백엔드(ShopMenuItems.normalizeHeader)가 다시 확인한다. 이름 · 링크는 서버가 대상에서 다시 정한다.
*/

const DEFAULT_LABELS: Record<string, string> = Object.fromEntries(DEFAULT_HEADER_MENU_ITEMS.map((item) => [item.key, item.label]));
const MAX_CUSTOM_MENUS = 20;
const MAX_LABEL_LENGTH = 20;

const TYPE_LABELS: Record<ShopMenuItemType, string> = {
  CATEGORY: "카테고리",
  PRODUCT: "상품",
  LINK: "직접 링크",
};

const isBuiltIn = (item: ShopMenuItem) => item.key in DEFAULT_LABELS;

/** 쇼핑몰 안 경로(/shop/...)나 http(s) 주소만 저장된다 (백엔드 isAllowedHref 와 같은 규칙) */
const isAllowedHref = (href: string) => {
  const value = href.trim();
  return value === "/shop" || value.startsWith("/shop/") || /^https?:\/\//i.test(value);
};

const newCustomKey = () => `CUSTOM_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`.replace(/[^A-Za-z0-9_]/g, "");

/** 기본 메뉴 링크 선택 상자의 특수 값 */
const LINK_DEFAULT = "__DEFAULT__";
const LINK_CUSTOM = "__CUSTOM__";

interface HeaderMenuEditorProps {
  items: ShopMenuItem[];
  onChange: (items: ShopMenuItem[]) => void;
  categories: CategoryDto[];
}

export const HeaderMenuEditor = ({ items, onChange, categories }: HeaderMenuEditorProps) => {
  const [isAdding, setIsAdding] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const categoryById = new Map(categories.map((category) => [category.id, category]));
  const productById = new Map(categories.flatMap((category) => category.products.map((product) => [product.id, product] as const)));
  const customCount = items.filter((item) => !isBuiltIn(item)).length;

  const patchItem = (key: string, patch: Partial<ShopMenuItem>) =>
    onChange(items.map((item) => (item.key === key ? { ...item, ...patch } : item)));
  const removeItem = (key: string) =>
    onChange(items.filter((item) => item.key !== key).map((item, order) => ({ ...item, displayOrder: order })));

  // 저장할 때 서버가 배열 순서를 displayOrder 로 확정하므로 배열 순서만 바꾼다
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = items.findIndex((item) => item.key === active.id);
    const to = items.findIndex((item) => item.key === over.id);
    if (from < 0 || to < 0) return;
    onChange(arrayMove(items, from, to).map((item, order) => ({ ...item, displayOrder: order })));
  };

  /** 카테고리 · 상품 메뉴는 지금 이름을 보여 준다 (쇼핑몰도 같은 이름을 쓴다) */
  const nameOf = (item: ShopMenuItem): MenuDisplay => {
    if (item.type === "CATEGORY") {
      const category = item.targetId != null ? categoryById.get(item.targetId) : undefined;
      // 숨긴 카테고리는 쇼핑몰이 메뉴를 그리지 않는다
      return { name: category?.name ?? item.label, missing: categories.length > 0 && !category, hiddenOnShop: !!category && !category.isEnable };
    }
    if (item.type === "PRODUCT") {
      const product = item.targetId != null ? productById.get(item.targetId) : undefined;
      return { name: product?.name ?? item.label, missing: categories.length > 0 && !product, hiddenOnShop: false };
    }
    return { name: isBuiltIn(item) ? DEFAULT_LABELS[item.key] : item.label, missing: false, hiddenOnShop: false };
  };

  return (
    <div className="flex flex-col gap-[0.5rem]">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((item) => item.key)} strategy={verticalListSortingStrategy}>
          <div className="flex flex-col gap-[0.375rem]">
            {items.map((item) => (
              <MenuRow
                key={item.key}
                item={item}
                display={nameOf(item)}
                categories={categories}
                onPatch={(patch) => patchItem(item.key, patch)}
                onRemove={isBuiltIn(item) ? undefined : () => removeItem(item.key)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {isAdding ? (
        <AddMenuPanel
          categories={categories}
          usedTargets={items}
          onCancel={() => setIsAdding(false)}
          onAdd={(item) => {
            onChange([...items, { ...item, displayOrder: items.length }]);
            setIsAdding(false);
          }}
        />
      ) : (
        <button
          type="button"
          disabled={customCount >= MAX_CUSTOM_MENUS}
          onClick={() => setIsAdding(true)}
          className="flex flex-row items-center justify-center gap-[0.25rem] h-[2.5rem] rounded-[8px] border border-dashed border-[#C9D6F2]
            text-[13px] text-[#4285F4] hover:bg-[#F5F8FF] disabled:text-[#BBBBBB] disabled:border-[#E0E0E0] disabled:hover:bg-transparent"
        >
          <LucidePlus className="w-4 h-4" />
          {customCount >= MAX_CUSTOM_MENUS ? `메뉴는 ${MAX_CUSTOM_MENUS}개까지 추가할 수 있습니다` : "메뉴 추가"}
        </button>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ *
 * 메뉴 한 줄
 * ------------------------------------------------------------------ */

interface MenuDisplay {
  name: string;
  /** 연결한 카테고리 · 상품이 지워졌다 */
  missing: boolean;
  /** 연결한 카테고리를 숨겨서 쇼핑몰에 메뉴가 보이지 않는다 */
  hiddenOnShop: boolean;
}

interface MenuRowProps {
  item: ShopMenuItem;
  display: MenuDisplay;
  categories: CategoryDto[];
  onPatch: (patch: Partial<ShopMenuItem>) => void;
  /** 추가 메뉴만 지울 수 있다 */
  onRemove?: () => void;
}

const Badge = ({ children, tone = "blue" }: { children: React.ReactNode; tone?: "blue" | "gray" | "warn" }) => (
  <span
    className={`shrink-0 px-[0.375rem] py-[1px] rounded-[4px] text-[11px] font-medium ${
      tone === "blue" ? "bg-[#E8EFFF] text-[#4285F4]" : tone === "warn" ? "bg-[#FFF4E5] text-[#B4560A]" : "bg-[#F5F5F5] text-[#888]"
    }`}
  >
    {children}
  </span>
);

const MenuRow = ({ item, display, categories, onPatch, onRemove }: MenuRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.key });
  const builtIn = isBuiltIn(item);
  const linkable = builtIn && LINKABLE_HEADER_MENU_KEYS.includes(item.key);
  const hrefInvalid = item.type === "LINK" && !!item.href && !isAllowedHref(item.href);

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className={`flex flex-col gap-[0.375rem] px-[0.625rem] py-[0.5rem] rounded-[8px] border border-[#EEEEEE] ${item.visible ? "bg-white" : "bg-[#FAFAFA]"}`}
    >
      <div className="flex flex-row items-center gap-[0.5rem] min-h-[2.25rem]">
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label={`${display.name} 순서 바꾸기`}
          className="shrink-0 text-[#BBBBBB] hover:text-[#555] cursor-grab active:cursor-grabbing"
        >
          <DragHandleIcon />
        </button>

        {item.type === "LINK" ? (
          <TextField
            aria-label="메뉴 이름"
            value={item.label}
            maxLength={MAX_LABEL_LENGTH}
            placeholder="메뉴 이름"
            onChange={(event) => onPatch({ label: event.target.value })}
          />
        ) : (
          <div className="flex-1 min-w-0 flex flex-row items-center gap-[0.375rem]">
            <span className={`truncate text-[13px] ${item.visible ? "text-[#222]" : "text-[#AAAAAA]"}`}>{display.name}</span>
            {builtIn ? <Badge tone="gray">기본</Badge> : <Badge>{TYPE_LABELS[item.type ?? "LINK"]}</Badge>}
            {display.missing ? <Badge tone="warn">연결 대상 없음</Badge> : null}
            {display.hiddenOnShop ? <Badge tone="warn">쇼핑몰 미노출</Badge> : null}
          </div>
        )}

        <ToggleSwitch size="sm" checked={item.visible} onChange={(visible) => onPatch({ visible })} label={`${display.name} 메뉴 노출`} />
        {onRemove ? (
          <button
            type="button"
            aria-label={`${display.name} 메뉴 삭제`}
            onClick={onRemove}
            className="shrink-0 w-[1.75rem] h-[1.75rem] flex items-center justify-center rounded-[6px] text-[#BBBBBB] hover:text-[#F24A4A] hover:bg-[#FEF2F2]"
          >
            <LucideX className="w-4 h-4" />
          </button>
        ) : null}
      </div>

      {item.type === "LINK" ? (
        <div className="pl-[1.5rem] flex flex-col gap-[0.25rem]">
          <TextField
            aria-label={`${display.name} 메뉴 주소`}
            value={item.href ?? ""}
            placeholder="/shop/... 또는 https://..."
            onChange={(event) => onPatch({ href: event.target.value })}
            className={hrefInvalid || !item.href ? "border-[#F24A4A]" : ""}
          />
          {hrefInvalid || !item.href ? (
            <span className="text-[12px] text-[#F24A4A]">/shop/ 으로 시작하는 쇼핑몰 주소나 http(s) 주소를 입력하세요.</span>
          ) : null}
        </div>
      ) : null}

      {linkable && item.visible ? <BuiltInLinkSelect item={item} categories={categories} onPatch={onPatch} /> : null}
    </div>
  );
};

/** 기본 메뉴(책자주문 등)가 이동할 곳: 기본 화면 · 카테고리 · 상품 · 주문/배송 조회 · 견적 문의 · 직접 입력 */
const BuiltInLinkSelect = ({ item, categories, onPatch }: { item: ShopMenuItem; categories: CategoryDto[]; onPatch: (patch: Partial<ShopMenuItem>) => void }) => {
  const href = item.href ?? null;
  const known =
    !href ||
    href === "/shop/quote-inquiry" ||
    href === "/shop/mypage/orders" ||
    categories.some((category) => `/shop/category/${category.id}` === href) ||
    categories.some((category) => category.products.some((product) => `/shop/product/detail/${product.id}` === href));
  const choice = !href ? LINK_DEFAULT : known ? href : LINK_CUSTOM;

  return (
    <div className="pl-[1.5rem] flex flex-col gap-[0.375rem]">
      <SelectField
        aria-label={`${DEFAULT_LABELS[item.key]} 메뉴 링크`}
        value={choice}
        onChange={(event) => {
          const value = event.target.value;
          if (value === LINK_CUSTOM) onPatch({ href: item.href ?? "/shop/" });
          else onPatch({ href: value === LINK_DEFAULT ? null : value });
        }}
      >
        <option value={LINK_DEFAULT}>기본 ({DEFAULT_MENU_HREF_LABELS[item.key] ?? "전체 상품"})</option>
        {categories.map((category) => (
          <option key={category.id} value={`/shop/category/${category.id}`}>
            카테고리: {category.name}
          </option>
        ))}
        {categories
          .filter((category) => category.products.length > 0)
          .map((category) => (
            <optgroup key={`products-${category.id}`} label={`상품 · ${category.name}`}>
              {category.products.map((product) => (
                <option key={product.id} value={`/shop/product/detail/${product.id}`}>
                  상품: {product.name}
                </option>
              ))}
            </optgroup>
          ))}
        <option value="/shop/mypage/orders">주문/배송 조회</option>
        <option value="/shop/quote-inquiry">견적 문의 페이지</option>
        <option value={LINK_CUSTOM}>직접 입력</option>
      </SelectField>
      {choice === LINK_CUSTOM ? (
        <TextField
          aria-label={`${DEFAULT_LABELS[item.key]} 메뉴 주소`}
          value={item.href ?? ""}
          placeholder="/shop/... 또는 https://..."
          onChange={(event) => onPatch({ href: event.target.value })}
        />
      ) : null}
    </div>
  );
};

/* ------------------------------------------------------------------ *
 * 메뉴 추가
 * ------------------------------------------------------------------ */

interface AddMenuPanelProps {
  categories: CategoryDto[];
  usedTargets: ShopMenuItem[];
  onAdd: (item: ShopMenuItem) => void;
  onCancel: () => void;
}

const AddMenuPanel = ({ categories, usedTargets, onAdd, onCancel }: AddMenuPanelProps) => {
  const [type, setType] = useState<ShopMenuItemType>("CATEGORY");
  const [targetId, setTargetId] = useState<number | null>(null);
  const [label, setLabel] = useState("");
  const [href, setHref] = useState("");

  const used = (menuType: ShopMenuItemType, id: number) => usedTargets.some((item) => item.type === menuType && item.targetId === id);
  const products = categories.flatMap((category) => category.products.map((product) => ({ ...product, categoryName: category.name })));

  const canAdd =
    type === "LINK" ? label.trim().length > 0 && label.trim().length <= MAX_LABEL_LENGTH && isAllowedHref(href) : targetId !== null;

  const submit = () => {
    if (!canAdd) return;
    const key = newCustomKey();
    if (type === "CATEGORY") {
      const category = categories.find((item) => item.id === targetId);
      onAdd({ key, type, targetId, label: category?.name ?? "", visible: true, displayOrder: 0, href: `/shop/category/${targetId}` });
    } else if (type === "PRODUCT") {
      const product = products.find((item) => item.id === targetId);
      onAdd({ key, type, targetId, label: product?.name ?? "", visible: true, displayOrder: 0, href: `/shop/product/detail/${targetId}` });
    } else {
      onAdd({ key, type, targetId: null, label: label.trim(), visible: true, displayOrder: 0, href: href.trim() });
    }
  };

  return (
    <div className="flex flex-col gap-[0.75rem] p-[0.75rem] rounded-[8px] border border-[#4285F4] bg-[#F5F8FF]">
      <Field label="연결할 곳">
        <Segmented
          ariaLabel="메뉴 종류"
          value={type}
          options={(["CATEGORY", "PRODUCT", "LINK"] as ShopMenuItemType[]).map((value) => ({ value, label: TYPE_LABELS[value] }))}
          onChange={(value) => {
            setType(value);
            setTargetId(null);
          }}
        />
      </Field>

      {type === "CATEGORY" ? (
        <Field label="카테고리" hint="메뉴 이름은 카테고리 이름을 그대로 씁니다. 마우스를 올리면 그 카테고리 상품이 주문 카드로 펼쳐집니다.">
          <SelectField aria-label="카테고리" value={targetId ?? ""} onChange={(event) => setTargetId(event.target.value ? Number(event.target.value) : null)}>
            <option value="">카테고리를 고르세요</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id} disabled={used("CATEGORY", category.id)}>
                {category.name}
                {used("CATEGORY", category.id) ? " (추가됨)" : !category.isEnable ? " (쇼핑몰 미노출)" : ""}
              </option>
            ))}
          </SelectField>
        </Field>
      ) : null}

      {type === "PRODUCT" ? (
        <Field label="상품" hint="메뉴 이름은 상품 이름을 그대로 쓰고, 누르면 그 상품 주문 화면으로 갑니다.">
          <SelectField aria-label="상품" value={targetId ?? ""} onChange={(event) => setTargetId(event.target.value ? Number(event.target.value) : null)}>
            <option value="">상품을 고르세요</option>
            {categories
              .filter((category) => category.products.length > 0)
              .map((category) => (
                <optgroup key={category.id} label={category.name}>
                  {category.products.map((product) => (
                    <option key={product.id} value={product.id} disabled={used("PRODUCT", product.id)}>
                      {product.name}
                      {used("PRODUCT", product.id) ? " (추가됨)" : ""}
                    </option>
                  ))}
                </optgroup>
              ))}
          </SelectField>
        </Field>
      ) : null}

      {type === "LINK" ? (
        <>
          <Field label={`메뉴 이름 (${label.trim().length}/${MAX_LABEL_LENGTH})`}>
            <TextField value={label} maxLength={MAX_LABEL_LENGTH} placeholder="예: 이벤트" onChange={(event) => setLabel(event.target.value)} />
          </Field>
          <Field label="주소" hint="쇼핑몰 안 주소(/shop/...)나 http(s) 주소">
            <TextField value={href} placeholder="/shop/... 또는 https://..." onChange={(event) => setHref(event.target.value)} />
          </Field>
        </>
      ) : null}

      <div className="flex flex-row justify-end gap-[0.5rem]">
        <button type="button" onClick={onCancel} className="h-[2.25rem] px-[0.875rem] rounded-[8px] text-[13px] text-[#555] hover:bg-white">
          취소
        </button>
        <button
          type="button"
          disabled={!canAdd}
          onClick={submit}
          className="h-[2.25rem] px-[1rem] rounded-[8px] bg-[#222] text-white text-[13px] font-medium hover:bg-[#333] disabled:bg-[#E0E0E0] disabled:text-[#AAAAAA]"
        >
          추가
        </button>
      </div>
    </div>
  );
};
